import React, { useEffect, useMemo } from 'react'
import TemplatePreviewBubble from '../TemplatePreviewBubble'
import useModelosAprovados from '../shared/disparo/useModelosAprovados'
import { motivoForaDoCorpo } from '../shared/disparo/SeletorDeModelo'
import { nomeDoTemplate, TEMPLATE_CATEGORY_LABELS } from '../../libs/metaTemplates'
import { variaveisDoTemplate, rotuloDaVariavel, aplicarVariaveis } from '../../libs/scheduledMessages'

// Passo "Mensagem" na API Oficial: o modelo aprovado, como ele chega no
// WhatsApp do primeiro cliente da lista e de onde vem cada campo do modelo.
//
// `valores` é indexado pelo NÚMERO da variável (valores[n - 1] é o {{n}}),
// a convenção do master. Cada valor é uma coluna da lista ("{{nome}}") ou um
// texto fixo. Modelo que pede algo fora do corpo (mídia no cabeçalho, link
// variável no botão) aparece desabilitado, com o porquê.

const SO_COLUNA = /^\{\{\s*([\w.-]+)\s*\}\}$/

function textoDoCorpo(template) {
  const corpo = (template?.components || []).find((c) => String(c?.type).toUpperCase() === 'BODY')
  return String(corpo?.text || '')
}

function modoDoValor(valor) {
  if (valor === undefined || valor === null) return ''
  const coluna = String(valor).match(SO_COLUNA)
  return coluna ? `col:${coluna[1]}` : 'fixo'
}

export default function EscolhaDeModelo({ api, secret, nome, valores = [], onChange, colunas = [], exemplo, onErro }) {
  const { modelos, carregando, erro } = useModelosAprovados(api, secret)

  const lista = useMemo(() => {
    const itens = modelos.map((t) => ({ template: t, nome: nomeDoTemplate(t), motivo: motivoForaDoCorpo(t) }))
    // Os que servem primeiro; os bloqueados ficam no fim, com o porquê.
    return itens.sort((a, b) => Number(!!a.motivo) - Number(!!b.motivo))
  }, [modelos])

  const atual = lista.find((m) => m.nome === nome && !m.motivo) || null
  const campos = useMemo(() => (atual ? variaveisDoTemplate(atual.template) : []), [atual])
  const disponiveis = ['nome', 'telefone', ...colunas.filter((c) => c !== 'nome' && c !== 'telefone')]

  useEffect(() => {
    if (erro && onErro) onErro(erro)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erro])

  const escolher = (m) => onChange({ nome: m.nome, valores: [], template: m.template })
  const preencher = (n, valor) => {
    const novo = [...valores]
    novo[n - 1] = valor
    onChange({ nome, valores: novo, template: atual?.template || null })
  }
  const trocarModo = (n, modo) => {
    if (!modo) preencher(n, undefined)
    else if (modo === 'fixo') preencher(n, '')
    else preencher(n, `{{${modo.slice(4)}}}`)
  }

  // Prévia com os dados do primeiro da lista: campo sem valor fica como {{n}}.
  const valoresDaPrevia = []
  campos.forEach((n) => {
    const v = valores[n - 1]
    if (typeof v === 'string' && v.trim()) valoresDaPrevia[n - 1] = aplicarVariaveis(v, exemplo)
  })
  const quem = exemplo?.name || exemplo?.phone

  if (carregando) return <p className="dp-extra">Carregando os modelos aprovados…</p>
  if (erro) return <p className="dp-erro">{erro}</p>
  if (!lista.length) {
    return (
      <p className="dp-extra">
        Nenhum modelo aprovado em português nesta conexão. Crie e aprove um em <b>Modelos Meta</b> para disparar.
      </p>
    )
  }

  return (
    <>
      <div className="dp-dois">
        <div className="dp-modelos" role="radiogroup" aria-label="Modelos aprovados">
          {lista.map((m) => {
            const categoria = String(m.template.category || '').toUpperCase()
            const marketing = categoria === 'MARKETING'
            return (
              <button
                key={m.nome}
                type="button"
                role="radio"
                aria-checked={m.nome === nome && !m.motivo}
                disabled={!!m.motivo}
                className="dp-modelo"
                onClick={() => escolher(m)}
              >
                <span className="dp-radio" />
                <span className="dp-modelo-nome">
                  {m.nome}
                  {TEMPLATE_CATEGORY_LABELS[categoria] && (
                    <span className={`dp-cat${marketing ? ' mkt' : ''}`}>
                      {TEMPLATE_CATEGORY_LABELS[categoria]}{marketing ? ' · custa mais' : ''}
                    </span>
                  )}
                </span>
                {m.motivo
                  ? <span className="dp-modelo-motivo">{m.motivo}</span>
                  : <span className="dp-modelo-texto">{textoDoCorpo(m.template)}</span>}
              </button>
            )
          })}
        </div>

        <div className="dp-telefone">
          {atual ? (
            <>
              <span className="dp-quem">{quem ? `Como chega para ${quem}` : 'Como chega para o cliente'}</span>
              <TemplatePreviewBubble template={atual.template} values={valoresDaPrevia} />
            </>
          ) : (
            <span className="dp-telefone-vazio">Escolha um modelo para ver como ele chega no WhatsApp do cliente.</span>
          )}
        </div>
      </div>

      {atual && campos.length > 0 && (
        <div className="dp-bloco">
          <h4>Preencha os campos do modelo</h4>
          <p className="dp-sub">Escolha de qual coluna da lista vem cada campo, ou escreva um texto fixo.</p>
          <div className="dp-mapa">
            {campos.map((n) => {
              const valor = valores[n - 1]
              const modo = modoDoValor(valor)
              const contexto = rotuloDaVariavel(atual.template, n)
              const exemploDoCampo = typeof valor === 'string' && valor.trim() ? aplicarVariaveis(valor, exemplo) : ''
              return (
                <React.Fragment key={n}>
                  <span className="dp-var">{`{{${n}}}`}</span>
                  <div className="dp-campo-fixo">
                    <select
                      className="dp-campo"
                      aria-label={`Campo {{${n}}}`}
                      value={modo}
                      onChange={(e) => trocarModo(n, e.target.value)}
                    >
                      <option value="">Escolha…</option>
                      {disponiveis.map((c) => <option key={c} value={`col:${c}`}>Coluna {c}</option>)}
                      <option value="fixo">Texto fixo</option>
                    </select>
                    {modo === 'fixo' && (
                      <input
                        className="dp-campo"
                        aria-label={`Texto fixo do campo {{${n}}}`}
                        placeholder="O mesmo texto para todos"
                        value={valor || ''}
                        onChange={(e) => preencher(n, e.target.value)}
                      />
                    )}
                  </div>
                  <span className="dp-ex" title={exemploDoCampo || contexto}>
                    {exemploDoCampo
                      ? `ex.: ${exemploDoCampo}`
                      : contexto.startsWith('Variável') ? '' : `depois de “${contexto}”`}
                  </span>
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
